import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiSun, FiMoon, FiGrid, FiChevronLeft, FiChevronRight, FiCalendar,
  FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiLock, FiUnlock, FiSearch, FiPrinter,
} from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import * as kassaService from '../services/kassaService';
import * as customerService from '../services/customerService';
import KassaGoldLedger from './KassaGoldLedger';
import KassaCloseModal from './KassaCloseModal';
import ReceiptPreview from '../components/ReceiptPreview';
import { getReceiptType } from '../config/receipts.config';
import '../gold-purchase.css';
import '../receipt-print.css';

/* =========================================================================
 * Helpers (pure)
 * ========================================================================= */

function money(n) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
}
function toISODate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDays(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return toISODate(dt);
}
function editVal(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) && n !== 0 ? String(v) : '';
}
// Format a unix-seconds timestamp as HH:MM (local time).
function timeHM(unixSeconds) {
  const n = parseInt(unixSeconds, 10);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
const CURR = ['usd', 'srd', 'eur'];
// Register-transaction receipt type (80mm thermal style, register layout).
const KASSA_RECEIPT_TYPE = { id: 'kassa_recibo', titleKey: 'rcpt_type_kassa', layout: 'kassa', fields: [] };
// Money in / money out per currency across all transaction types.
//  recebido -> in;  pagamento -> out;  cambio -> received in, given back out.
function inOutTotals(txs) {
  const inn = { usd: 0, srd: 0, eur: 0 };
  const out = { usd: 0, srd: 0, eur: 0 };
  for (const tx of txs) {
    if (tx.type === 'cambio') {
      const rc = (tx.recv_currency || '').toLowerCase();
      if (inn[rc] !== undefined) inn[rc] += parseFloat(tx.recv_amount) || 0;
      out.usd += parseFloat(tx.amount_usd) || 0;
      out.srd += parseFloat(tx.amount_srd) || 0;
      out.eur += parseFloat(tx.amount_eur) || 0;
    } else {
      // custody add = cash in; custody subtract = cash out.
      const inflow = tx.type === 'recebido' || (tx.type === 'custody' && tx.custody_dir === 'add');
      const bucket = inflow ? inn : out;
      bucket.usd += parseFloat(tx.amount_usd) || 0;
      bucket.srd += parseFloat(tx.amount_srd) || 0;
      bucket.eur += parseFloat(tx.amount_eur) || 0;
    }
  }
  return { inn, out };
}

/* =========================================================================
 * Component
 * ========================================================================= */

export default function KassaDashboard({ register = 'main', showGold = true }) {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { goToPicker } = useModule();

  const [date, setDate] = useState(() => toISODate(new Date()));
  const [view, setView] = useState('kassa'); // 'kassa' | 'gold'

  const [transactions, setTransactions] = useState([]);
  const [openingSet, setOpeningSet] = useState(false);
  const [opening, setOpening] = useState({ usd: '', srd: '', eur: '' });
  const [goldPaid, setGoldPaid] = useState({ usd: 0, srd: 0, eur: 0 });
  const [closed, setClosed] = useState(false);

  // Register draft row.
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  // Type filter for the current day's list ('' = all).
  const [listFilter, setListFilter] = useState('');
  // Customers (for the custody-type client picker + name display).
  const [customers, setCustomers] = useState([]);

  // Print: gold receipt (printRow, from the gold ledger) or a register
  // transaction receipt (printTx).
  const [printRow, setPrintRow] = useState(null);
  const [printTx, setPrintTx] = useState(null);
  const [printing, setPrinting] = useState(false);

  // Close-register ("Kassa afsluiten") modal.
  const [showClose, setShowClose] = useState(false);
  // Unlock (reopen) password prompt.
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockPw, setUnlockPw] = useState('');
  const [unlockErr, setUnlockErr] = useState('');
  const [unlockBusy, setUnlockBusy] = useState(false);

  // Global transaction search.
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  // ---- Live sync ----
  const lastVersion = useRef('');
  const pendingSync = useRef(false);
  const dateRef = useRef(date); dateRef.current = date;
  const draftRef = useRef(draft); draftRef.current = draft;
  const openingBusy = useRef(false);
  const printingRef = useRef(printing); printingRef.current = printing;

  // Load customers once (for the custody client picker + name display).
  useEffect(() => {
    (async () => {
      const res = await customerService.listCustomers();
      if (res.success && Array.isArray(res.data)) setCustomers(res.data);
    })();
  }, []);
  const customerName = (id) => {
    const c = customers.find((x) => String(x.id) === String(id));
    return c ? c.name : (id ? `#${id}` : '');
  };

  const applyDay = (data) => {
    setOpeningSet(!!data.opening_set);
    setClosed(!!data.closed);
    setTransactions(data.transactions || []);
    setGoldPaid(data.gold_paid || { usd: 0, srd: 0, eur: 0 });
    if (data.opening) {
      setOpening({ usd: String(data.opening.usd ?? ''), srd: String(data.opening.srd ?? ''), eur: String(data.opening.eur ?? '') });
    } else {
      setOpening({ usd: '', srd: '', eur: '' });
    }
  };

  const loadDay = async (iso = dateRef.current) => {
    const res = await kassaService.getDay(iso, register);
    if (res.success && res.data) {
      applyDay(res.data);
      const v = await kassaService.getDayVersion(iso, register);
      if (v.success && v.data) lastVersion.current = v.data.version;
    }
  };

  useEffect(() => {
    loadDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Poll kassa/day-version (folds in gold changes) every 4s.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.visibilityState !== 'visible' || printingRef.current) return;
      const iso = dateRef.current;
      const v = await kassaService.getDayVersion(iso, register);
      if (cancelled || !v.success || !v.data) return;
      if (v.data.version === lastVersion.current) return;
      if (draftRef.current || openingBusy.current) pendingSync.current = true;
      else await loadDay(iso);
    };
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draft && pendingSync.current) {
      pendingSync.current = false;
      loadDay(dateRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  /* ---- Saldo (computed live: opening + in − out − gold paid) ---- */
  const { inn: received, out: paid } = useMemo(() => inOutTotals(transactions), [transactions]);
  const base = { usd: parseFloat(opening.usd) || 0, srd: parseFloat(opening.srd) || 0, eur: parseFloat(opening.eur) || 0 };
  const saldo = {
    usd: base.usd + received.usd - paid.usd - (goldPaid.usd || 0),
    srd: base.srd + received.srd - paid.srd - (goldPaid.srd || 0),
    eur: base.eur + received.eur - paid.eur - (goldPaid.eur || 0),
  };
  const hasGoldPaid = (goldPaid.usd || 0) !== 0 || (goldPaid.srd || 0) !== 0 || (goldPaid.eur || 0) !== 0;

  /* ---- Opening balance ---- */
  const commitOpening = async (next) => {
    openingBusy.current = false;
    const res = await kassaService.setOpening(date, {
      usd: parseFloat(next.usd) || 0, srd: parseFloat(next.srd) || 0, eur: parseFloat(next.eur) || 0,
    }, register);
    if (res.success) { setOpeningSet(true); lastVersion.current = ''; }
    else alert(res.message || 'Failed to save opening balances');
  };
  const setOpeningField = (c, val) => setOpening((o) => ({ ...o, [c]: val }));

  /* ---- Register draft ---- */
  const startAdd = () => setDraft({ id: null, type: 'recebido', description: '', usd: '', srd: '', eur: '', recvCur: 'usd', recvAmount: '', custodyCustomerId: '', custodyClientText: '', custodyDir: 'add' });
  const startEdit = (tx) => setDraft({
    id: tx.id, type: tx.type, description: tx.description || '',
    usd: tx.amount_usd, srd: tx.amount_srd, eur: tx.amount_eur,
    recvCur: (tx.recv_currency || 'usd').toLowerCase(),
    recvAmount: tx.recv_amount,
    custodyCustomerId: tx.custody_customer_id || '',
    custodyClientText: tx.custody_customer_id ? customerName(tx.custody_customer_id) : '',
    custodyDir: tx.custody_dir || 'add',
  });
  const setDraftField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const cancelDraft = () => setDraft(null);

  const confirmDraft = async () => {
    if (!draft || saving) return;
    const usd = parseFloat(draft.usd) || 0;
    const srd = parseFloat(draft.srd) || 0;
    const eur = parseFloat(draft.eur) || 0;
    const isCambio = draft.type === 'cambio';
    const isCustody = draft.type === 'custody';
    const recvAmount = parseFloat(draft.recvAmount) || 0;

    if (isCambio) {
      if (recvAmount === 0) { alert(t('kassa_cambio_need_recv')); return; }
      if (usd === 0 && srd === 0 && eur === 0) { alert(t('kassa_cambio_need_given')); return; }
    } else if (isCustody) {
      if (!draft.custodyCustomerId) { alert(t('kassa_custody_need_client')); return; }
      if (usd === 0 && srd === 0 && eur === 0) { alert(t('kassa_need_amount')); return; }
    } else if (usd === 0 && srd === 0 && eur === 0) {
      alert(t('kassa_need_amount')); return;
    }

    const payload = isCambio
      ? { usd, srd, eur, recvCurrency: draft.recvCur, recvAmount }
      : isCustody
        ? { usd, srd, eur, custodyCustomerId: draft.custodyCustomerId, custodyDir: draft.custodyDir }
        : { usd, srd, eur, recvCurrency: null, recvAmount: 0 };

    setSaving(true);
    try {
      let res;
      if (draft.id === null) {
        res = await kassaService.addTransaction(date, draft.type, draft.description.trim(), payload, register);
      } else {
        res = await kassaService.updateTransaction(draft.id, { type: draft.type, description: draft.description.trim(), ...payload }, register);
      }
      if (res.success) { setDraft(null); await loadDay(date); }
      else alert(res.message || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const deleteTx = async (id) => {
    if (!window.confirm(t('delete_tx_confirm'))) return;
    const res = await kassaService.deleteTransaction(id, register);
    if (res.success) await loadDay(date);
    else alert(res.message || 'Failed to delete transaction');
  };

  const openUnlock = () => { setUnlockPw(''); setUnlockErr(''); setShowUnlock(true); };
  const submitUnlock = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (unlockBusy) return;
    setUnlockBusy(true);
    setUnlockErr('');
    try {
      const res = await kassaService.reopenClosing(date, unlockPw, register);
      if (res.success) {
        setShowUnlock(false);
        setUnlockPw('');
        lastVersion.current = '';
        await loadDay(date);
      } else {
        setUnlockErr(res.message || t('kassa_wrong_password'));
      }
    } finally {
      setUnlockBusy(false);
    }
  };

  /* ---- Global search (debounced while the modal is open) ---- */
  useEffect(() => {
    if (!showSearch) return;
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      const res = await kassaService.searchTransactions(searchQ, searchType, {}, register);
      if (res.success && res.data) setSearchResults(res.data.transactions || []);
      else setSearchResults([]);
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(handle);
  }, [showSearch, searchQ, searchType, register]);

  const openSearch = () => { setSearchQ(''); setSearchType(''); setSearchResults([]); setShowSearch(true); };
  const jumpToTx = (tx) => {
    setShowSearch(false);
    setListFilter('');          // clear filter so the row is visible
    setHighlightId(tx.id);
    setTimeout(() => setHighlightId(null), 4000);
    if (tx.kassa_date && tx.kassa_date !== date) setDate(tx.kassa_date);
  };

  /* ---- Date nav ---- */
  const goPrev = () => setDate((d) => addDays(d, -1));
  const goNext = () => setDate((d) => addDays(d, 1));
  const goToday = () => setDate(toISODate(new Date()));
  const isToday = date === toISODate(new Date());

  /* ---- Print (gold receipts) ---- */
  const receiptType = useMemo(() => getReceiptType('compra_ouro'), []);
  const printValues = useMemo(() => {
    if (!printRow) return {};
    return {
      note_number: printRow.note_number,
      peso: printRow.grams,
      preco: printRow.price_per_gram,
      total: printRow.total_usd,
      pago_usd: printRow.paid_usd,
      pago_srd: printRow.paid_srd,
      pago_eur: printRow.paid_eur,
    };
  }, [printRow]);

  // Register transaction receipt values.
  const printTxValues = useMemo(() => {
    if (!printTx) return {};
    const isCustody = printTx.type === 'custody';
    const custodyAdd = isCustody && printTx.custody_dir === 'add';
    const label = printTx.type === 'cambio' ? t('kassa_cambio')
      : isCustody ? t('kassa_custody')
      : printTx.type === 'recebido' ? t('kassa_recebido') : t('kassa_pagamento');
    const d = new Date(parseInt(printTx.created_at, 10) * 1000);
    // Custody prints client name + direction in the description; sign follows direction.
    const custodyDesc = isCustody
      ? `${customerName(printTx.custody_customer_id)} · ${custodyAdd ? t('kassa_custody_add') : t('kassa_custody_sub')}${printTx.description ? ' · ' + printTx.description : ''}`
      : (printTx.description || '');
    return {
      title: label,
      kind: isCustody ? (custodyAdd ? 'recebido' : 'pagamento') : printTx.type,
      description: custodyDesc,
      datetime: isNaN(d.getTime()) ? '' : d.toLocaleString(),
      amount_usd: printTx.amount_usd,
      amount_srd: printTx.amount_srd,
      amount_eur: printTx.amount_eur,
      recv_currency: printTx.recv_currency,
      recv_amount: printTx.recv_amount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printTx, t, customers]);

  const runPrint = () => {
    setPrinting(true);
    setTimeout(() => {
      const done = () => { setPrinting(false); setPrintRow(null); setPrintTx(null); window.removeEventListener('afterprint', done); };
      window.addEventListener('afterprint', done);
      window.print();
      setTimeout(done, 1500);
    }, 150);
  };
  const handlePrint = (row) => { setPrintTx(null); setPrintRow(row); runPrint(); };
  const handlePrintTx = (tx) => { setPrintRow(null); setPrintTx(tx); runPrint(); };

  // Print mode: render only the receipt (receipt-print.css handles print sizing).
  if (printing) {
    return (
      <div className="gp-print-only">
        {printTx
          ? <ReceiptPreview type={KASSA_RECEIPT_TYPE} values={printTxValues} currency="USD" t={t} />
          : <ReceiptPreview type={receiptType} values={printValues} currency="USD" t={t} />}
      </div>
    );
  }

  return (
    <div className="layout">
      <header className="app-header">
        <div className="brand">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="Logo" />
          <h1 className="app-title">{t('brand_title')}</h1>
        </div>
        <div className="app-actions">
          <button className="btn btn-ghost btn-icon" onClick={goToPicker} title={t('back_to_modules')} aria-label={t('back_to_modules')}>
            <FiGrid />
          </button>
          <button className="btn btn-ghost btn-icon" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} aria-label="Toggle theme">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <select className="lang-select" value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language" title="Language">
            <option value="en">English</option>
            <option value="pt-BR">Português (BR)</option>
          </select>
          <span className="muted">{t('signed_in_as')} {user?.username}</span>
          <button className="btn btn-ghost" onClick={logout}>{t('logout')}</button>
        </div>
      </header>

      <main className="content gp-content">
        {/* Date navigation + view tabs */}
        <div className="gp-datebar">
          <button className="btn btn-ghost btn-icon" onClick={goPrev} title={t('prev_day')}><FiChevronLeft /></button>
          <div className="gp-date-display">
            <FiCalendar />
            <input
              type="date"
              className="gp-date-input"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
            />
          </div>
          <button className="btn btn-ghost btn-icon" onClick={goNext} title={t('next_day')}><FiChevronRight /></button>
          {!isToday && <button className="btn btn-ghost btn-sm" onClick={goToday}>{t('today')}</button>}
          {(!showGold || view === 'kassa') && (
            <button className="btn btn-ghost btn-sm" onClick={openSearch} title={t('kassa_search')}>
              <FiSearch style={{ marginRight: '6px' }} /> {t('kassa_search')}
            </button>
          )}

          {closed ? (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="kassa-closed-badge"><FiLock /> {t('kassa_closed')}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowClose(true)}>{t('kassa_close_title')}</button>
              <button className="btn btn-primary btn-sm" onClick={openUnlock}><FiUnlock style={{ marginRight: '6px' }} /> {t('kassa_unlock')}</button>
            </div>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setShowClose(true)} style={{ marginLeft: 'auto' }}>
              {t('kassa_close_title')}
            </button>
          )}

          {showGold && (
            <div className="segmented kassa-view-switch" role="tablist" aria-label="View">
              <button type="button" className={'segmented-item' + (view === 'kassa' ? ' active' : '')} onClick={() => setView('kassa')} role="tab" aria-selected={view === 'kassa'}>
                {t('module_kassa')}
              </button>
              <button type="button" className={'segmented-item' + (view === 'gold' ? ' active' : '')} onClick={() => setView('gold')} role="tab" aria-selected={view === 'gold'}>
                {t('kassa_gold_tab')}
              </button>
            </div>
          )}
        </div>

        {/* Saldo bar — register view only (the Gold tab has its own summary and
            needs the vertical space for rows). */}
        {(!showGold || view === 'kassa') && (
        <div className="gp-balance-bar">
          <div className="gp-bal-group">
            <span className="gp-bal-label">
              {t('kassa_opening')}
              {!openingSet && <span className="muted small"> ({t('kassa_not_set')})</span>}
            </span>
            <div className="gp-bal-inline">
              {CURR.map((c) => (
                <span className="gp-bal-chip" key={c} style={{ gap: '4px' }}>
                  <span className="gp-bal-code">{c.toUpperCase()}</span>
                  <input
                    className="gp-cell" style={{ width: '92px' }}
                    type="number" step="0.01" inputMode="decimal" placeholder="0.00"
                    value={opening[c]}
                    disabled={closed}
                    onFocus={() => { openingBusy.current = true; }}
                    onChange={(e) => setOpeningField(c, e.target.value)}
                    onBlur={() => commitOpening({ ...opening, [c]: opening[c] })}
                  />
                </span>
              ))}
            </div>
          </div>

          <span className="gp-bal-sep gp-bal-sep-plus">+</span>
          <div className="gp-bal-group">
            <span className="gp-bal-label">{t('kassa_recebido')}</span>
            <BalInline bal={received} tone="pos" />
          </div>

          <span className="gp-bal-sep">−</span>
          <div className="gp-bal-group">
            <span className="gp-bal-label">{t('kassa_pagamento')}</span>
            <BalInline bal={paid} tone="neg" />
          </div>

          {showGold && hasGoldPaid && (
            <>
              <span className="gp-bal-sep">−</span>
              <div className="gp-bal-group">
                <span className="gp-bal-label">{t('kassa_gold_paid')}</span>
                <BalInline bal={goldPaid} tone="neg" />
              </div>
            </>
          )}

          <span className="gp-bal-sep">=</span>
          <div className="gp-bal-group gp-bal-group-gold">
            <span className="gp-bal-label">{t('kassa_saldo')}</span>
            <BalInline bal={saldo} strong />
          </div>
        </div>
        )}

        {/* Body: register OR gold ledger */}
        {(!showGold || view === 'kassa') ? (
          <div className="gp-table-wrap">
            {/* Type filter for the current day's list */}
            <div className="segmented kassa-list-filter" role="tablist" aria-label={t('type')} style={{ marginBottom: '10px', display: 'inline-flex' }}>
              {[['', t('all')], ['recebido', t('kassa_recebido')], ['pagamento', t('kassa_pagamento')], ['cambio', t('kassa_cambio')], ['custody', t('kassa_custody')]].map(([val, label]) => (
                <button
                  key={val || 'all'} type="button" role="tab"
                  className={'segmented-item' + (listFilter === val ? ' active' : '')}
                  aria-selected={listFilter === val}
                  onClick={() => setListFilter(val)}
                >
                  {label}
                </button>
              ))}
            </div>
            <table className="gp-table">
              <thead>
                <tr>
                  <th style={{ width: '70px' }}>{t('kassa_time')}</th>
                  <th style={{ width: '130px' }}>{t('type')}</th>
                  <th>{t('description')}</th>
                  <th className="gp-num gp-paid-col">USD</th>
                  <th className="gp-num gp-paid-col">SRD</th>
                  <th className="gp-num gp-paid-col">EUR</th>
                  <th className="gp-actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const shown = listFilter ? transactions.filter((tx) => tx.type === listFilter) : transactions;
                  if (shown.length === 0 && !draft) {
                    return <tr><td colSpan={7} className="gp-empty muted">{listFilter ? t('no_results') : t('no_transactions')}</td></tr>;
                  }
                  return shown.map((tx) => {
                  if (draft && draft.id === tx.id) {
                    return <RegisterDraftRow key={tx.id} draft={draft} saving={saving} customers={customers} onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />;
                  }
                  const isCambio = tx.type === 'cambio';
                  const isCustody = tx.type === 'custody';
                  const isRec = tx.type === 'recebido';
                  const custodyAdd = isCustody && tx.custody_dir === 'add';
                  const inflow = isRec || custodyAdd; // + for saldo
                  const sign = inflow ? '+' : '−';
                  const pillClass = isCambio ? 'cambio' : (isCustody ? 'custody' : (isRec ? 'pos' : 'neg'));
                  const pillLabel = isCambio ? t('kassa_cambio') : (isCustody ? t('kassa_custody') : (isRec ? t('kassa_recebido') : t('kassa_pagamento')));
                  const recvCur = (tx.recv_currency || '').toLowerCase();
                  return (
                    <tr key={tx.id} className={highlightId === tx.id ? 'gp-row-highlight' : undefined}>
                      <td className="gp-ro kassa-time">{timeHM(tx.created_at)}</td>
                      <td>
                        <span className={'kassa-pill ' + pillClass}>{pillLabel}</span>
                      </td>
                      <td className="kassa-desc">
                        {isCambio ? (
                          <span className="gp-gold-pos">↓ {recvCur.toUpperCase()} {money(tx.recv_amount)}</span>
                        ) : isCustody ? (
                          <span>
                            <strong>{customerName(tx.custody_customer_id)}</strong>
                            {` · ${custodyAdd ? t('kassa_custody_add') : t('kassa_custody_sub')}`}
                            {tx.description ? ` · ${tx.description}` : ''}
                          </span>
                        ) : (tx.description || '—')}
                      </td>
                      {CURR.map((c) => {
                        const v = parseFloat(tx['amount_' + c]) || 0;
                        // cambio: the 3 columns are money GIVEN back (always negative).
                        const cls = v === 0 ? 'gp-ro' : (inflow ? 'gp-gold-pos' : 'gp-gold-neg');
                        const s = isCambio ? '−' : sign;
                        return (
                          <td key={c} className={'gp-num gp-paid-col ' + cls}>
                            {v === 0 ? '—' : `${s} ${money(v)}`}
                          </td>
                        );
                      })}
                      <td className="gp-actions-col">
                        <div className="gp-row-actions">
                          <button className="btn btn-ghost btn-icon" title={t('edit')} onClick={() => startEdit(tx)} disabled={!!draft || closed}><FiEdit2 /></button>
                          <button className="btn btn-ghost btn-icon" title={t('receipt')} onClick={() => handlePrintTx(tx)} disabled={!!draft}><FiPrinter /></button>
                          <button className="btn btn-ghost btn-icon" title={t('delete')} onClick={() => deleteTx(tx.id)} disabled={!!draft || closed}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  );
                  });
                })()}
                {draft && draft.id === null && (
                  <RegisterDraftRow draft={draft} saving={saving} customers={customers} onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />
                )}
              </tbody>
              {transactions.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="gp-foot-label" colSpan={3}>{t('kassa_saldo')}</td>
                    <td className="gp-num gp-paid-col gp-foot-total">$ {money(saldo.usd)}</td>
                    <td className="gp-num gp-paid-col gp-foot-total">SRD {money(saldo.srd)}</td>
                    <td className="gp-num gp-paid-col gp-foot-total">€ {money(saldo.eur)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>

            {closed ? (
              <div className="kassa-locked-note muted"><FiLock style={{ marginRight: '6px' }} /> {t('kassa_locked_note')}</div>
            ) : (
              <button className="btn btn-primary gp-add-btn" onClick={startAdd} disabled={!!draft}>
                <FiPlus style={{ marginRight: '6px' }} /> {t('kassa_add_line')}
              </button>
            )}
          </div>
        ) : (
          <KassaGoldLedger date={date} t={t} locked={closed} onChanged={() => loadDay(date)} onPrint={handlePrint} onDate={setDate} />
        )}
      </main>

      {showClose && (
        <KassaCloseModal
          date={date}
          saldo={saldo}
          register={register}
          t={t}
          onClose={() => setShowClose(false)}
          onSaved={() => loadDay(date)}
        />
      )}

      {showSearch && (
        <div className="modal-backdrop" onClick={() => setShowSearch(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="kassaSearchTitle" style={{ maxWidth: '1000px', width: '95%' }}>
            <div className="modal-header">
              <h3 id="kassaSearchTitle"><FiSearch style={{ marginRight: '8px' }} />{t('kassa_search_title')}</h3>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <input
                  type="text" autoFocus
                  className="search-input"
                  style={{ flex: '1 1 260px' }}
                  placeholder={t('kassa_search_ph')}
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                />
                <select className="currency-select" value={searchType} onChange={(e) => setSearchType(e.target.value)} aria-label={t('type')}>
                  <option value="">{t('all')}</option>
                  <option value="recebido">{t('kassa_recebido')}</option>
                  <option value="pagamento">{t('kassa_pagamento')}</option>
                  <option value="cambio">{t('kassa_cambio')}</option>
                </select>
              </div>

              <div className="muted small" style={{ marginBottom: '8px' }}>
                {searchLoading ? t('loading') : `${searchResults.length} ${t('results')}`}
              </div>

              <div style={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '10px' }}>
                <table className="gp-table" style={{ minWidth: '760px' }}>
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('kassa_time')}</th>
                      <th>{t('type')}</th>
                      <th>{t('description')}</th>
                      <th className="gp-num gp-paid-col">USD</th>
                      <th className="gp-num gp-paid-col">SRD</th>
                      <th className="gp-num gp-paid-col">EUR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.length === 0 && !searchLoading && (
                      <tr><td colSpan={7} className="gp-empty muted">{t('no_results')}</td></tr>
                    )}
                    {searchResults.map((tx) => {
                      const isCambio = tx.type === 'cambio';
                      const isRec = tx.type === 'recebido';
                      const pillClass = isCambio ? 'cambio' : (isRec ? 'pos' : 'neg');
                      const pillLabel = isCambio ? t('kassa_cambio') : (isRec ? t('kassa_recebido') : t('kassa_pagamento'));
                      const s = isCambio ? '−' : (isRec ? '+' : '−');
                      return (
                        <tr key={tx.id} style={{ cursor: 'pointer' }} onClick={() => jumpToTx(tx)} title={t('kassa_jump_day')}>
                          <td className="gp-ro">{tx.kassa_date}</td>
                          <td className="gp-ro kassa-time">{timeHM(tx.created_at)}</td>
                          <td><span className={'kassa-pill ' + pillClass}>{pillLabel}</span></td>
                          <td className="kassa-desc">
                            {isCambio
                              ? <span className="gp-gold-pos">↓ {(tx.recv_currency || '').toUpperCase()} {money(tx.recv_amount)}</span>
                              : (tx.description || '—')}
                          </td>
                          {CURR.map((c) => {
                            const v = parseFloat(tx['amount_' + c]) || 0;
                            const cls = v === 0 ? 'gp-ro' : (isCambio ? 'gp-gold-neg' : (isRec ? 'gp-gold-pos' : 'gp-gold-neg'));
                            return <td key={c} className={'gp-num gp-paid-col ' + cls}>{v === 0 ? '—' : `${s} ${money(v)}`}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowSearch(false)}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}

      {showUnlock && (
        <div className="modal-backdrop" onClick={() => !unlockBusy && setShowUnlock(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="kassaUnlockTitle" style={{ maxWidth: '380px' }}>
            <div className="modal-header">
              <h3 id="kassaUnlockTitle"><FiUnlock style={{ marginRight: '8px' }} />{t('kassa_unlock')}</h3>
              <div className="muted small">{t('kassa_unlock_prompt')}</div>
            </div>
            <form className="modal-body" onSubmit={submitUnlock}>
              <label>
                <span>{t('kassa_password')}</span>
                <input
                  type="password" autoFocus autoComplete="off"
                  value={unlockPw}
                  onChange={(e) => { setUnlockPw(e.target.value); setUnlockErr(''); }}
                  placeholder="••••••••"
                />
              </label>
              {unlockErr && <div className="error" style={{ color: 'var(--negative)' }}>{unlockErr}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowUnlock(false)} disabled={unlockBusy}>{t('cancel')}</button>
                <button type="submit" className="btn btn-primary" disabled={unlockBusy || !unlockPw}>
                  <FiUnlock style={{ marginRight: '6px' }} /> {unlockBusy ? t('loading') : t('kassa_unlock')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Register draft row (add / edit -> confirm) ---- */
function RegisterDraftRow({ draft, saving, customers = [], onField, onConfirm, onCancel, t }) {
  const onKey = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); onConfirm(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  const isCambio = draft.type === 'cambio';
  const isCustody = draft.type === 'custody';
  return (
    <tr className="gp-draft-row" onKeyDown={onKey}>
      <td className="gp-ro kassa-time muted">—</td>
      <td>
        <select className="gp-cell" value={draft.type} onChange={(e) => onField('type', e.target.value)}>
          <option value="recebido">{t('kassa_recebido')}</option>
          <option value="pagamento">{t('kassa_pagamento')}</option>
          <option value="cambio">{t('kassa_cambio')}</option>
          <option value="custody">{t('kassa_custody')}</option>
        </select>
        {isCustody && (
          <select className="gp-cell" style={{ marginTop: '6px' }} value={draft.custodyDir} onChange={(e) => onField('custodyDir', e.target.value)}>
            <option value="add">{t('kassa_custody_add')}</option>
            <option value="subtract">{t('kassa_custody_sub')}</option>
          </select>
        )}
      </td>
      <td>
        {isCambio ? (
          // Cambio: first the amount + currency RECEIVED.
          <div className="kassa-recv-group">
            <span className="kassa-recv-label">{t('kassa_received')}</span>
            <input className="gp-cell" type="number" step="0.01" inputMode="decimal" autoFocus
              value={editVal(draft.recvAmount)} onChange={(e) => onField('recvAmount', e.target.value)} placeholder="0.00" />
            <select className="gp-cell" value={draft.recvCur} onChange={(e) => onField('recvCur', e.target.value)}>
              {CURR.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
          </div>
        ) : isCustody ? (
          // Custody: pick a client, then (optional) description.
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <input className="gp-cell kassa-desc-cell" type="text" list="kassa-custody-clients" autoFocus
              value={draft.custodyClientText !== undefined ? draft.custodyClientText : ''}
              onChange={(e) => {
                const val = e.target.value;
                const match = customers.find((c) => c.name === val);
                onField('custodyClientText', val);
                onField('custodyCustomerId', match ? match.id : '');
              }}
              placeholder={t('kassa_custody_client')} />
            <datalist id="kassa-custody-clients">
              {customers.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
            <input className="gp-cell kassa-desc-cell" type="text"
              value={draft.description} onChange={(e) => onField('description', e.target.value)} placeholder={t('description_placeholder')} />
          </div>
        ) : (
          <input className="gp-cell kassa-desc-cell" type="text" autoFocus
            value={draft.description} onChange={(e) => onField('description', e.target.value)} placeholder={t('description_placeholder')} />
        )}
      </td>
      {CURR.map((c) => (
        <td key={c} className="gp-num gp-paid-col">
          <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
            value={editVal(draft[c])} onChange={(e) => onField(c, e.target.value)}
            placeholder={isCambio ? t('kassa_given_ph') : '0.00'} title={isCambio ? t('kassa_given_hint') : undefined} />
        </td>
      ))}
      <td className="gp-actions-col">
        <div className="gp-row-actions">
          <button className="btn btn-primary btn-icon" title={t('kassa_confirm')} onClick={onConfirm} disabled={saving}><FiCheck /></button>
          <button className="btn btn-ghost btn-icon" title={t('cancel')} onClick={onCancel} disabled={saving}><FiX /></button>
        </div>
      </td>
    </tr>
  );
}

// Inline balance: three currency chips on one line.
function BalInline({ bal, tone, strong }) {
  return (
    <div className={'gp-bal-inline' + (tone ? ` tone-${tone}` : '')}>
      {CURR.map((c) => (
        <span className="gp-bal-chip" key={c}>
          <span className="gp-bal-code">{c.toUpperCase()}</span>
          <span className={'gp-bal-val' + (strong ? ' gp-gold-strong' : '')}>
            {bal[c] < 0 ? '−' : ''}{money(Math.abs(bal[c]))}
          </span>
        </span>
      ))}
    </div>
  );
}
